import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

// Catalog screens (Categories/Sub-categories/Brands) render their own Snackbar,
// so mutations opt out of the global interceptor's duplicate SUCCESS toast. The
// global ERROR toast stays on as a safety net.
const SN = { skipNotify: 'success' };

export const fetchAdminCategories = createAsyncThunk(
  'catalog/fetchCategories',
  async ({ page = 1, limit = 25, search = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/categories', { params: { page, limit, search } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch categories');
    }
  }
);

// The FULL category list for dropdowns (Sub-categories / Brands forms).
// It used to share state.catalog.categories with the paginated Categories
// table, so a dropdown opened after visiting Categories listed only that table
// page (5 rows) — a newly created category was "not found" — and the table
// could briefly show the dropdown's full list instead of its own page.
export const fetchCategoryOptions = createAsyncThunk(
  'catalog/fetchCategoryOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/categories', { params: { all: true } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch categories');
    }
  }
);

export const createAdminCategory = createAsyncThunk(
  'catalog/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/categories', categoryData, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to create category');
    }
  }
);

export const updateAdminCategory = createAsyncThunk(
  'catalog/updateCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/admin/categories/${id}`, data, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update category');
    }
  }
);

export const deleteAdminCategory = createAsyncThunk(
  'catalog/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/admin/categories/${id}`, SN);
      return { id, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete category');
    }
  }
);

export const bulkDeleteAdminCategories = createAsyncThunk(
  'catalog/bulkDeleteCategories',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/categories/bulk-delete', { ids }, SN);
      return { ids, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete categories');
    }
  }
);

export const createAdminSubCategory = createAsyncThunk(
  'catalog/createSubCategory',
  async (subCategoryData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/sub-categories', subCategoryData, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to create sub-category');
    }
  }
);

export const updateAdminSubCategory = createAsyncThunk(
  'catalog/updateSubCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/admin/sub-categories/${id}`, data, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update sub-category');
    }
  }
);

export const deleteAdminSubCategory = createAsyncThunk(
  'catalog/deleteSubCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/admin/sub-categories/${id}`, SN);
      return { id, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete sub-category');
    }
  }
);

export const bulkDeleteAdminSubCategories = createAsyncThunk(
  'catalog/bulkDeleteSubCategories',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/sub-categories/bulk-delete', { ids }, SN);
      return { ids, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete sub-categories');
    }
  }
);

export const createAdminBrand = createAsyncThunk(
  'catalog/createBrand',
  async (brandData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/brands', brandData, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to create brand');
    }
  }
);

export const updateAdminBrand = createAsyncThunk(
  'catalog/updateBrand',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/admin/brands/${id}`, data, SN);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update brand');
    }
  }
);

export const deleteAdminBrand = createAsyncThunk(
  'catalog/deleteBrand',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/admin/brands/${id}`, SN);
      return { id, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete brand');
    }
  }
);

export const bulkDeleteAdminBrands = createAsyncThunk(
  'catalog/bulkDeleteBrands',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/brands/bulk-delete', { ids }, SN);
      return { ids, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete brands');
    }
  }
);

export const fetchAdminSubCategories = createAsyncThunk(
  'catalog/fetchSubCategories',
  async ({ page = 1, limit = 25, search = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/sub-categories', { params: { page, limit, search } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch sub-categories');
    }
  }
);

export const fetchAdminBrands = createAsyncThunk(
  'catalog/fetchBrands',
  async ({ page = 1, limit = 25, search = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/brands', { params: { page, limit, search } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch brands');
    }
  }
);


const initialState = {
  categories: { data: [], total: 0, loading: false, error: null, requestId: null },
  categoryOptions: { data: [], loading: false, error: null },
  subCategories: { data: [], total: 0, loading: false, error: null, requestId: null },
  brands: { data: [], total: 0, loading: false, error: null, requestId: null },
};

const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminCategories.pending, (state, action) => { state.categories.loading = true; state.categories.error = null; state.categories.requestId = action.meta.requestId; })
      .addCase(fetchAdminCategories.fulfilled, (state, action) => {
        // Paging quickly fires several requests; a slower, OLDER response must
        // not overwrite the page the admin is now on (looked like duplicates).
        if (action.meta.requestId !== state.categories.requestId) return;
        state.categories.loading = false;
        state.categories.data = action.payload.categories || [];
        state.categories.total = action.payload.totalCategories || 0;
      })
      .addCase(fetchAdminCategories.rejected, (state, action) => { if (action.meta.requestId !== state.categories.requestId) return; state.categories.loading = false; state.categories.error = action.payload; })
      
      .addCase(fetchAdminSubCategories.pending, (state, action) => { state.subCategories.loading = true; state.subCategories.error = null; state.subCategories.requestId = action.meta.requestId; })
      .addCase(fetchAdminSubCategories.fulfilled, (state, action) => {
        // Paging quickly fires several requests; a slower, OLDER response must
        // not overwrite the page the admin is now on (looked like duplicates).
        if (action.meta.requestId !== state.subCategories.requestId) return;
        state.subCategories.loading = false;
        state.subCategories.data = action.payload.subCategories || [];
        state.subCategories.total = action.payload.totalSubCategories || 0;
      })
      .addCase(fetchAdminSubCategories.rejected, (state, action) => { if (action.meta.requestId !== state.subCategories.requestId) return; state.subCategories.loading = false; state.subCategories.error = action.payload; })
      
      .addCase(fetchAdminBrands.pending, (state, action) => { state.brands.loading = true; state.brands.error = null; state.brands.requestId = action.meta.requestId; })
      .addCase(fetchAdminBrands.fulfilled, (state, action) => {
        // Paging quickly fires several requests; a slower, OLDER response must
        // not overwrite the page the admin is now on (looked like duplicates).
        if (action.meta.requestId !== state.brands.requestId) return;
        state.brands.loading = false;
        state.brands.data = action.payload.brands || [];
        state.brands.total = action.payload.totalBrands || 0;
      })
      .addCase(fetchCategoryOptions.pending, (state) => { state.categoryOptions.loading = true; state.categoryOptions.error = null; })
      .addCase(fetchCategoryOptions.fulfilled, (state, action) => {
        state.categoryOptions.loading = false;
        state.categoryOptions.data = action.payload.categories || [];
      })
      .addCase(fetchCategoryOptions.rejected, (state, action) => { state.categoryOptions.loading = false; state.categoryOptions.error = action.payload; })

      .addCase(fetchAdminBrands.rejected, (state, action) => { if (action.meta.requestId !== state.brands.requestId) return; state.brands.loading = false; state.brands.error = action.payload; });
  },
});

export default catalogSlice.reducer;
